/**
 * Builders for the embedded `history` array written directly onto
 * regularization request docs (companyDatabase/{companyId}/regularizationRequests/{requestId})
 * and onto the session doc a request produces on approval. Every entry is
 * appended inside the same Firestore transaction as the state change it
 * describes — this module only builds the entry object, callers own where
 * and when it gets written.
 *
 * Mirror of the eventType/entityType string values lives in
 * functions/time-log-history-helpers.js for the session-write side, which
 * has no import path into this package (functions/ and src/ are separate
 * deployed packages) — keep the string values byte-identical by hand.
 */
import firebase from '@config/firebase.config';

const Timestamp = firebase.firestore.Timestamp;

export const ENTITY_TYPES = Object.freeze({
  SESSION: 'SESSION',
  REGULARIZATION_REQUEST: 'REGULARIZATION_REQUEST',
});

/** Full enum for reference — this package only ever writes CREATED_BY_REGULARIZATION. */
export const SESSION_EVENT_TYPES = Object.freeze({
  LIVE_STARTED: 'SESSION_LIVE_STARTED',
  COMPLETED: 'SESSION_COMPLETED',
  BACKFILLED: 'SESSION_BACKFILLED',
  CREATED_BY_REGULARIZATION: 'SESSION_CREATED_BY_REGULARIZATION',
});

export const REGULARIZATION_EVENT_TYPES = Object.freeze({
  CREATED: 'REGULARIZATION_CREATED',
  UPDATED: 'REGULARIZATION_UPDATED',
  APPROVED: 'REGULARIZATION_APPROVED',
  DECLINED: 'REGULARIZATION_DECLINED',
  DELETED: 'REGULARIZATION_DELETED',
});

/**
 * Always array-append, never arrayUnion — arrayUnion's dedup-on-append would
 * silently drop an entry that happens to deep-equal an earlier one instead
 * of appending it.
 */
export function appendHistoryEntry(existingHistory, entry) {
  return [...(existingHistory || []), entry];
}

function requestEntry({
  eventType,
  requestId,
  userId,
  actor,
  effectiveStart,
  effectiveEnd,
  before,
  after,
  linkedEntityId = null,
}) {
  return {
    entityType: ENTITY_TYPES.REGULARIZATION_REQUEST,
    entityId: requestId,
    userId,
    eventType,
    actor,
    occurredAt: Timestamp.now(),
    effectiveStart,
    effectiveEnd,
    before,
    after,
    linkedEntityId,
  };
}

export function buildRegularizationCreatedEntry({
  requestId,
  userId,
  actor,
  effectiveStart,
  effectiveEnd,
  after,
}) {
  return requestEntry({
    eventType: REGULARIZATION_EVENT_TYPES.CREATED,
    requestId,
    userId,
    actor,
    effectiveStart,
    effectiveEnd,
    before: null,
    after,
  });
}

export function buildRegularizationUpdatedEntry({
  requestId,
  userId,
  actor,
  effectiveStart,
  effectiveEnd,
  before,
  after,
}) {
  return requestEntry({
    eventType: REGULARIZATION_EVENT_TYPES.UPDATED,
    requestId,
    userId,
    actor,
    effectiveStart,
    effectiveEnd,
    before,
    after,
  });
}

export function buildRegularizationApprovedEntry({
  requestId,
  userId,
  actor,
  effectiveStart,
  effectiveEnd,
  linkedEntityId,
}) {
  return requestEntry({
    eventType: REGULARIZATION_EVENT_TYPES.APPROVED,
    requestId,
    userId,
    actor,
    effectiveStart,
    effectiveEnd,
    before: { status: 'Pending' },
    after: { status: 'Approved' },
    linkedEntityId,
  });
}

export function buildRegularizationDeclinedEntry({
  requestId,
  userId,
  actor,
  effectiveStart,
  effectiveEnd,
}) {
  return requestEntry({
    eventType: REGULARIZATION_EVENT_TYPES.DECLINED,
    requestId,
    userId,
    actor,
    effectiveStart,
    effectiveEnd,
    before: { status: 'Pending' },
    after: { status: 'Declined' },
  });
}

export function buildRegularizationDeletedEntry({
  requestId,
  userId,
  actor,
  effectiveStart,
  effectiveEnd,
  before,
}) {
  return requestEntry({
    eventType: REGULARIZATION_EVENT_TYPES.DELETED,
    requestId,
    userId,
    actor,
    effectiveStart,
    effectiveEnd,
    before,
    after: { status: 'Deleted' },
  });
}

/**
 * The session created by an approved request carries its own entry plus
 * (separately, by the caller) a copy of the request's whole history so far.
 */
export function buildSessionCreatedByRegularizationEntry({
  sessionId,
  userId,
  actor,
  effectiveStart,
  effectiveEnd,
  linkedEntityId,
}) {
  return {
    entityType: ENTITY_TYPES.SESSION,
    entityId: sessionId,
    userId,
    eventType: SESSION_EVENT_TYPES.CREATED_BY_REGULARIZATION,
    actor,
    occurredAt: Timestamp.now(),
    effectiveStart,
    effectiveEnd,
    before: null,
    after: { status: 'completed', pastData: true, source: 'regularization' },
    linkedEntityId,
  };
}
